// PostsWidget.jsx

import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setPosts } from "state";
import PostWidget from "./PostWidget";
import { Box, Skeleton, Typography, useMediaQuery } from "@mui/material";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const PostsWidget = ({
  userId,
  isProfile = false,
  sort = "date",
  refreshKey = 0,
  excludePostId = null,
}) => {
  const dispatch = useDispatch();
  const posts = useSelector((state) => state.posts) || [];
  const token = useSelector((state) => state.token);
  const [loadedQueryKey, setLoadedQueryKey] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const isNonMobileScreens = useMediaQuery("(min-width:600px)");
  const queryKey = useMemo(
    () =>
      isProfile
        ? `profile:${userId || "none"}:${token ? "auth" : "anon"}:${refreshKey}`
        : `feed:${sort}:${token ? "auth" : "anon"}:${refreshKey}`,
    [isProfile, userId, sort, token, refreshKey]
  );

  const getPosts = useCallback(async () => {
    try {
      const headers = token
        ? { Authorization: `Bearer ${token}` }
        : {};

      const response = await fetch(`${BASE_URL}/posts?sort=${sort}`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        console.error("Failed to fetch posts:", response.statusText);
        return [];
      }

      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("An error occurred while fetching posts:", error);
      return [];
    }
  }, [sort, token]);

  const getUserPosts = useCallback(async () => {
    if (!userId) {
      return [];
    }
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(
        `${BASE_URL}/posts/${userId}/posts`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        console.error("Failed to fetch user posts:", response.statusText);
        return [];
      }

      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("An error occurred while fetching user posts:", error);
      return [];
    }
  }, [token, userId]);

  useEffect(() => {
    let isCancelled = false;

    const fetchPosts = async () => {
      setIsLoading(true);
      const data = isProfile ? await getUserPosts() : await getPosts();
      if (isCancelled) return;
      dispatch(setPosts({ posts: data }));
      setLoadedQueryKey(queryKey);
      setIsLoading(false);
    };

    if (isProfile && !userId) {
      dispatch(setPosts({ posts: [] }));
      setLoadedQueryKey(queryKey);
      setIsLoading(false);
      return () => {
        isCancelled = true;
      };
    }

    fetchPosts();

    return () => {
      isCancelled = true;
    };
  }, [dispatch, getPosts, getUserPosts, isProfile, queryKey, userId]);

  const isCurrentQueryReady = loadedQueryKey === queryKey && !isLoading;
  const visiblePosts = Array.isArray(posts)
    ? posts.filter((post) => post._id !== excludePostId)
    : [];
  const skeletonCount = isNonMobileScreens ? 6 : 3;

  return (
    <Box
      display="grid"
      gridTemplateColumns={
        isNonMobileScreens ? "repeat(3, 1fr)" : "repeat(1, 1fr)"
      }
      gap="1rem"
    >
      {!isCurrentQueryReady ? (
        Array.from({ length: skeletonCount }).map((_, index) => (
          <Box key={`post-skeleton-${index}`} bgcolor="background.alt" borderRadius="12px" overflow="hidden">
            <Box p={1} display="flex" alignItems="center" gap={1}>
              <Skeleton variant="circular" width={36} height={36} />
              <Box flex={1}>
                <Skeleton width="45%" height={16} />
                <Skeleton width="30%" height={12} />
              </Box>
            </Box>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                "&:after": { content: '""', display: "block", paddingBottom: "100%" },
              }}
            >
              <Skeleton
                variant="rectangular"
                sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
              />
            </Box>
            <Box p={1}>
              <Skeleton width="35%" height={20} />
              <Skeleton width="80%" height={16} />
            </Box>
          </Box>
        ))
      ) : visiblePosts.length > 0 ? (
        visiblePosts.map(
          (
            {
              _id,
              userId,
              caption,
              description,
              firstName,
              lastName,
              username,
              artistName,
              school,
              authorSchool,
              isOfficial,
              userPicturePath,
              picturePath,
              imageUrls,
              likes,
              comments,
              createdAt,
            }
          ) => (
            <PostWidget
              key={_id}
              postId={_id}
              postUserId={userId}
              caption={caption || description}
              firstName={firstName}
              lastName={lastName}
              username={username}
              artistName={artistName}
              school={school}
              authorSchool={authorSchool}
              isOfficial={Boolean(isOfficial)}
              userPicturePath={userPicturePath}
              picturePath={picturePath}
              imageUrls={imageUrls}
              likes={likes}
              comments={comments}
              createdAt={createdAt}
            />
          )
        )
      ) : (
        <Typography>Keine Kaleidoskope verfügbar.</Typography>
      )}
    </Box>
  );
};

export default PostsWidget;
